import { createApi } from '@reduxjs/toolkit/query/react';
import { KeypointParams, KeypointFramesPage } from 'models/keypoints';
import { baseQueryWithAuthRedirect } from './rtk';
import { ViewLabelFramesPage, ViewLabelParams } from 'models/view-labels';

export interface ViewLabelStats {
  total_found: number;
  accepted: number;
  rejected: number;
  to_be_reviewed: number;
}

export const keypointsAPI = createApi({
  reducerPath: 'keypointsAPI',
  baseQuery: baseQueryWithAuthRedirect,
  tagTypes: ['KeypointFrames', 'ViewLabelFrames'], // Ensure ViewLabelFrames is here
  endpoints: builder => ({
    getKeypointFrames: builder.query<KeypointFramesPage, KeypointParams>({
      query: (args: KeypointParams) => {
        const params = new URLSearchParams();
        params.append('page', String(args.page));
        params.append('size', String(args.size));

        if (args.view && args.view.uuid) params.append('view_class', args.view.uuid);
        if (args.features && args.features.length > 0) {
          args.features.forEach(f => params.append('keypoint_collection_class', f.uuid));
        }

        if (args.advancedFilters) {
          if (args.advancedFilters.datasources && args.advancedFilters.datasources.uuid)
            params.append('datasource', args.advancedFilters.datasources.uuid);
          if (args.advancedFilters.dicomICID)
            params.append('dicom', args.advancedFilters.dicomICID);
          if ((args.advancedFilters as any).frameId)
            params.append('frame_id', (args.advancedFilters as any).frameId);
          if (
            (args.advancedFilters as any).reviewStatus &&
            (args.advancedFilters as any).reviewStatus.length > 0
          )
            params.append('review_status', (args.advancedFilters as any).reviewStatus.join(','));
          if (
            args.advancedFilters.study &&
            typeof args.advancedFilters.study === 'string' &&
            args.advancedFilters.study.trim()
          ) {
            params.append('study', args.advancedFilters.study);
          }
          if (args.advancedFilters.keypointICID)
            params.append('keypoint_collection_icid', args.advancedFilters.keypointICID);
          if (args.advancedFilters.technician && args.advancedFilters.technician.length > 0) {
            args.advancedFilters.technician.forEach(t => params.append('user', t.uuid));
          }
          if (args.advancedFilters.created_at_after)
            params.append('created_at_after', args.advancedFilters.created_at_after);
          if (args.advancedFilters.created_at_before)
            params.append('created_at_before', args.advancedFilters.created_at_before);
          if (args.advancedFilters.updated_at_after)
            params.append('updated_at_after', args.advancedFilters.updated_at_after);
          if (args.advancedFilters.updated_at_before)
            params.append('updated_at_before', args.advancedFilters.updated_at_before);
          // Annotation set name (may be present on advancedFilters typed as AdvancedFiltersOptions)
          const asName = (args.advancedFilters as any).annotationSetName as string | undefined;
          if (asName && typeof asName === 'string' && asName.trim()) {
            params.append('annotation_set_name', asName);
          }
        }

        return {
          url: `api/v1/keypoint-frames/?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['KeypointFrames'],
    }),
    getViewLabelFrames: builder.query<ViewLabelFramesPage, ViewLabelParams>({
      query: (args: ViewLabelParams) => {
        const params = new URLSearchParams();
        params.append('page', String(args.page));
        params.append('size', String(args.size));

        if (args.view && args.view.uuid) params.append('view_class', args.view.uuid); // Modified
        if (args.features && args.features.length > 0) {
          // Features are not directly applicable to ViewLabels, keeping for consistency if needed elsewhere
          args.features.forEach(f => params.append('features', f.uuid));
        }

        if (args.advancedFilters) {
          Object.entries(args.advancedFilters).forEach(([key, value]) => {
            if (value) {
              if (Array.isArray(value)) {
                if (key === 'technician') {
                  // Special handling for technician
                  value.forEach(item => params.append('user', item.uuid)); // Modified
                } else {
                  value.forEach(item => params.append(key, item.uuid));
                }
              } else if (typeof value === 'object' && value !== null && 'uuid' in value) {
                if (key === 'datasources') {
                  if (typeof value === 'string') {
                    // Added to handle string datasources
                    params.append('datasource', value);
                  } else {
                    params.append('datasource', (value as { name: string }).name); // Modified to send name
                  }
                } else if (key === 'study') {
                  params.append('study', (value as { uuid: string }).uuid);
                } else {
                  params.append(key, (value as { uuid: string }).uuid);
                }
              } else {
                // Map camelCase advanced filter keys to backend snake_case params where needed
                if (key === 'dicomICID') {
                  params.append('dicom', String(value));
                } else if (key === 'viewLabelICID') {
                  params.append('icid', String(value));
                } else if (key === 'created_at_after') {
                  params.append('created_at_after', String(value));
                } else if (key === 'created_at_before') {
                  params.append('created_at_before', String(value));
                } else if (key === 'updated_at_after') {
                  params.append('updated_at_after', String(value));
                } else if (key === 'updated_at_before') {
                  params.append('updated_at_before', String(value));
                } else if (key === 'annotationSetName') {
                  // backend expects snake_case param
                  params.append('annotation_set_name', String(value));
                } else {
                  params.append(key, String(value));
                }
              }
            }
          });
        }
        return {
          url: 'api/v1/view-label-dicoms/',
          params,
        };
      },
      providesTags: ['ViewLabelFrames'],
    }),
    getViewLabelStats: builder.query<ViewLabelStats, void>({
      query: () => {
        return {
          url: 'api/v1/view-label-dicoms/stats/',
        };
      },
    }),
  }),
});

export const {
  useGetKeypointFramesQuery,
  useLazyGetKeypointFramesQuery,
  useGetViewLabelFramesQuery,
  useLazyGetViewLabelFramesQuery,
  useGetViewLabelStatsQuery,
} = keypointsAPI;
